import { DateOffset } from '../../legacy/sb-form-configuration/sb-form-context';
import { unitOfTime } from 'moment';
import moment from 'moment';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { Inject, Injectable, InjectionToken, LOCALE_ID, Optional } from '@angular/core';
import { subMinutes } from 'date-fns';

// ISO 8601 - JSON parsed version of JS datetime.
const dateFormat = new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/);

/** @deprecated - use date-fns instead
 * Function for getting a date based on an existing one, offset with a number of weekdays.
 *
 * @param date Date to use as base for date offsetting
 * @param amount Number of days to offset
 * @param shouldIncludeWeekends if true, function will count Saturday and Sunday as part of offset.
 * Set to false if you don't want to a return date set to a Saturday or Sunday.
 * @returns A new Date offset with "amount" number of days.
 */
function getDateWithDayOffset(
    date: Date,
    amount: number = 0,
    shouldIncludeWeekends?: boolean
) {
    let mom = moment(date);
    if (!shouldIncludeWeekends) {
        while (amount > 0) {
            mom = mom.add(1, 'days');
            if (![0, 6].includes(mom.weekday())) {
                amount--;
            }
        }
        return mom.toDate();
    }

    return mom.add(amount, 'days').toDate();
}

/** @deprecated - use date-fns instead */
function getDateFromOffset(dateOffset: DateOffset, date: Date): Date {
    if (dateOffset.startOfUnit) {
        return getStartDateOfUnit(date, dateOffset.unit as unitOfTime.StartOf);
    } else if (dateOffset.endOfUnit) {
        return getEndDateOfUnit(date, dateOffset.unit as unitOfTime.StartOf);
    } else {
        return getDeltaOfUnit(
            date,
            dateOffset.unit as unitOfTime.DurationConstructor,
            dateOffset.offset
        );
    }
}

/** @deprecated - use date-fns instead */
function getDeltaOfUnit(
    date: Date,
    unit: unitOfTime.DurationConstructor,
    offset: number
): Date {
    return offset < 0
        ? moment(date).subtract(-offset, unit).toDate()
        : moment(date).add(offset, unit).toDate();
}

/** @deprecated - use date-fns instead */
function getStartDateOfUnit(date: Date, unit: unitOfTime.StartOf): Date {
    return moment(date).startOf(unit).toDate();
}

/** @deprecated - use date-fns instead */
function getEndDateOfUnit(date: Date, unit: unitOfTime.StartOf) {
    return moment(date).endOf(unit).toDate();
}

/** @deprecated - use date-fns instead
 * jsonParseDateSerializer
 * Handles JSON date string to DateTime JS object conversion. */
function jsonParseDateSerializer(key, value) {
    if (typeof value === 'string' && dateFormat.test(value)) {
        return new Date(value);
    }

    return value;
}

export {
    getDateWithDayOffset,
    getDateFromOffset,
    getDeltaOfUnit,
    getStartDateOfUnit,
    getEndDateOfUnit,
    jsonParseDateSerializer
};

/** setFirstHourOfDayUTC
 * @description set the time of the date to the first hour of the day
 * @param { Date } date
 * @returns date with time set to 00:00:00.000
 */
export const setFirstHourOfDayUTC = (date: Date) => {
    return new Date(
        new Date(subMinutes(date, new Date(date).getTimezoneOffset())).setUTCHours(
            0,
            0,
            0,
            0
        )
    );
};

/** setLastHourOfDayUTC
 * @description set the time of the date to the last hour of the day
 * @param { Date } date
 * @returns date with time set to 23:59:59.999
 */
export const setLastHourOfDayUTC = (date: Date) => {
    return new Date(
        new Date(subMinutes(date, new Date(date).getTimezoneOffset())).setUTCHours(
            23,
            59,
            59,
            999
        )
    );
};

/** convertLocalDateToUTC
 * @description convert locale date time to UTC
 * @param {Date} date
 * @returns {Date} date in UTC
 * @example convertDateToUTC(startOfDay(new Date())) // returns 2020-07-01T00:00:00.000Z whereas startOfDay(new Date()) returns 2020-06-31T22:00:00.000Z if locale is UTC/GMT+2 like in Stockholm.
 */
export const convertLocalDateToUTC = (date: Date) => {
    return new Date(
        Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
        )
    );
};

export interface CustomDateAdapterConfig {
    useUTC: boolean; // if set to true, the date adapter will use UTC dates instead of local dates when creating dates
}

export const CUSTOM_DATE_ADAPTER_CONFIG = new InjectionToken<CustomDateAdapterConfig>(
    'custom date adapter config'
);

@Injectable()
export class CustomDateAdapter extends DateFnsAdapter {
    constructor(
        @Inject(LOCALE_ID) public override locale: Locale,
        @Optional()
        @Inject(CUSTOM_DATE_ADAPTER_CONFIG)
        private readonly _config: CustomDateAdapterConfig
    ) {
        super(locale);
    }
    createDate(year: number, month: number, date: number): Date {
        return this._config?.useUTC
            ? setFirstHourOfDayUTC(new Date(year, month, date))
            : super.createDate(year, month, date);
    }

    format(date: Date, displayFormat: any): string {
        return this._config?.useUTC
            ? super.format(setFirstHourOfDayUTC(new Date(date)), displayFormat)
            : super.format(date, displayFormat);
    }

    parse(value: any, parseFormat: any): Date | null {
        const date = super.parse(value, parseFormat);
        return this._config?.useUTC ? setFirstHourOfDayUTC(date) : date;
    }
}
